import React, { useEffect, useMemo, useState } from 'react';
import { scenarioInternalApi, scenarioLibraryInternalApi, scenarioMetricsApi } from '../apis';
import {
  FindAllScenarioMetricsPeriodTypeEnum,
  InternalApiScenarioEntityType,
  InternalApiScenarioMetric,
  InternalApiScenarioStatus,
  InternalApiScenarioTypeV2,
  InternalApiSortDirection,
} from '../api-internal';

import SalvPage from '../common/SalvPage';
import { getConfigurationUrl } from '../navigation/helper';
import { useLocation, useSearchParams } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import {
  SalvButton,
  SalvCard,
  SalvTab,
  SalvTable,
  SalvTablePaginationType,
  SalvTabList,
  SalvTabPanel,
  SalvTabs,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import ScenarioFilters, { ScenarioEntityFilterType, ScenarioFiltersState } from './ScenarioFilters';
import { convertToDropdownOption } from '../common/helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import './MonitoringScenarios.scss';
import { statusSwitchers } from './MonitoringScenario';
import { faCircleInfo, faPlus } from '@fortawesome/pro-solid-svg-icons';
import NewScenarioModal from './NewScenarioModal';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useMe } from '../organisation/useMe';
import { CAN_ADD_SCENARIO } from '../auth/permissions';
import { getLibraryScenarioColumns, getScenarioColumns, getScenarioMetricColumns } from './helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConfigurationProduct } from '../settings/configuration/Configuration';

export const SCENARIO_PAGE_SIZE = 20;
export const METRIC_PAGE_SIZE = 5;
const PARAM_TAB = 'tab';

export const monitoringScenarioTypes: { [key: string]: string } = {
  [InternalApiScenarioTypeV2.REAL_TIME]: 'Real-time',
  [InternalApiScenarioTypeV2.POST_EVENT]: 'Post-event',
  [InternalApiScenarioTypeV2.PERIODIC]: 'Periodic',
  [InternalApiScenarioTypeV2.PERIODIC_ENTITY]: 'Periodic entity',
};
const MonitoringScenarios = () => {
  const [scenarioMetrics, setScenarioMetrics] = useState<InternalApiScenarioMetric[]>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNewScenarioModal, setShowNewScenarioModal] = useState(false);
  const initialTab = searchParams.get(PARAM_TAB) || 'scenarios';
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const { currentOrganisation, features, permissions } = useMe();
  const navigate = useNavigate();
  const location = useLocation();

  const canAddScenario = permissions.includes(CAN_ADD_SCENARIO);
  const isPeriodicEntityTypeEnabled = isFeatureEnabled(ORGANISATION_FEATURE.SCENARIO_PERIODIC_ENTITY_TYPE, features);

  const getQueryFilterOptions = (name: string, options: DropdownOption[]) => {
    const query = searchParams.get(name)?.split('&');
    return options.filter((o) => (query || []).includes(o.value));
  };

  const statusOptions = Object.keys(InternalApiScenarioStatus).map((status) =>
    convertToDropdownOption(statusSwitchers[status as InternalApiScenarioStatus].name, status)
  );
  const typeOptions = Object.keys(InternalApiScenarioTypeV2)
    .filter((type) => isPeriodicEntityTypeEnabled || type !== InternalApiScenarioTypeV2.PERIODIC_ENTITY)
    .map((type) => convertToDropdownOption(monitoringScenarioTypes[type as InternalApiScenarioTypeV2], type));

  const [filters, setFilters] = useState<ScenarioFiltersState>({
    entity:
      ScenarioEntityFilterType[searchParams.get('entity') as keyof typeof ScenarioEntityFilterType] ||
      ScenarioEntityFilterType.ALL,

    status: getQueryFilterOptions('status', statusOptions),
    type: getQueryFilterOptions('type', typeOptions),
    categories: [],
  });

  const { data: categoryOptions } = useQuery({
    queryKey: ['categoryOptions'],
    queryFn: async () => {
      const categoryOptions = (await scenarioInternalApi.findAllScenarioCategories()).map((category) =>
        convertToDropdownOption(category.name, category.id)
      );

      setFilters((prev) => ({ ...prev, categories: getQueryFilterOptions('categories', categoryOptions) }));
      return categoryOptions;
    },
    initialData: [],
  });

  useQuery({
    queryKey: ['scenarioMetrics', filters, sorting, currentOrganisation?.id],
    queryFn: () => fetchScenarioMetrics(METRIC_PAGE_SIZE, filters),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    enabled: selectedTab === 'scenarios',
  });

  useEffect(() => {
    searchParams.set(PARAM_TAB, selectedTab);
    setSearchParams(searchParams, {
      state: location.state,
      replace: true,
    });
  }, [selectedTab]);

  const fetchScenarios = (page: number, size: number, filters: ScenarioFiltersState) => {
    return scenarioInternalApi.findAllScenarios({
      page,
      size,
      types: (filters && filters.type.length > 0
        ? filters.type.map((status) => status.key)
        : isPeriodicEntityTypeEnabled
          ? Object.keys(InternalApiScenarioTypeV2)
          : Object.keys(InternalApiScenarioTypeV2).filter(
              (type) => type !== InternalApiScenarioTypeV2.PERIODIC_ENTITY
            )) as InternalApiScenarioTypeV2[],
      entityType:
        filters.entity === ScenarioEntityFilterType.ALL
          ? undefined
          : (filters.entity.toString() as InternalApiScenarioEntityType),
      statuses: (filters && filters.status.length > 0
        ? filters.status.map((status) => status.key)
        : Object.keys(InternalApiScenarioStatus)) as InternalApiScenarioStatus[],
      categoryIds: filters && filters.categories.length > 0 ? filters.categories.map((c) => c.key) : undefined,
      query: filters.query || undefined,
      orderByStatus:
        sorting.find((s) => s.id === 'status')?.desc === false
          ? InternalApiSortDirection.ASC
          : InternalApiSortDirection.DESC,
      orderByUpdatedTime:
        sorting.find((s) => s.id === 'updatedTime')?.desc === false
          ? InternalApiSortDirection.ASC
          : InternalApiSortDirection.DESC,
    });
  };

  const fetchScenarioMetrics = async (size: number, filters: ScenarioFiltersState) => {
    const newScenarioMetrics = await scenarioMetricsApi.findAllScenarioMetrics({
      size,
      types: (filters && filters.type.length > 0
        ? filters.type.map((status) => status.key)
        : isPeriodicEntityTypeEnabled
          ? Object.keys(InternalApiScenarioTypeV2)
          : Object.keys(InternalApiScenarioTypeV2).filter(
              (type) => type !== InternalApiScenarioTypeV2.PERIODIC_ENTITY
            )) as InternalApiScenarioTypeV2[],
      entityType:
        filters.entity === ScenarioEntityFilterType.ALL
          ? undefined
          : (filters.entity.toString() as InternalApiScenarioEntityType),
      statuses: (filters && filters.status.length > 0
        ? filters.status.map((status) => status.key)
        : Object.keys(InternalApiScenarioStatus)) as InternalApiScenarioStatus[],
      categoryIds: filters && filters.categories.length > 0 ? filters.categories.map((c) => c.key) : undefined,
      periodType: FindAllScenarioMetricsPeriodTypeEnum.ONE_MONTH_BEFORE,
    });

    setScenarioMetrics(newScenarioMetrics);
    return newScenarioMetrics;
  };

  const fetchLibraryScenarios = (page: number, size: number, filters: ScenarioFiltersState) => {
    return scenarioLibraryInternalApi.findAllLibraryScenarios({
      page,
      size,
      types: (filters && filters.type.length > 0
        ? filters.type.map((status) => status.key)
        : isPeriodicEntityTypeEnabled
          ? Object.keys(InternalApiScenarioTypeV2)
          : Object.keys(InternalApiScenarioTypeV2).filter(
              (type) => type !== InternalApiScenarioTypeV2.PERIODIC_ENTITY
            )) as InternalApiScenarioTypeV2[],
      entityType:
        filters.entity === ScenarioEntityFilterType.ALL
          ? undefined
          : (filters.entity.toString() as InternalApiScenarioEntityType),
      categoryNames: filters && filters.categories.length > 0 ? filters.categories.map((c) => c.label) : undefined,
      query: filters.query || undefined,
    });
  };

  const scenariosColumns = useMemo(() => getScenarioColumns(), []);
  const scenariosMetricsColumns = useMemo(() => getScenarioMetricColumns(), []);
  const libraryScenariosColumns = useMemo(() => getLibraryScenarioColumns(), []);

  const { data, fetchNextPage } = useInfiniteQuery({
    queryKey: ['scenarios', filters, sorting, currentOrganisation?.id],
    queryFn: ({ pageParam }) => fetchScenarios(pageParam, SCENARIO_PAGE_SIZE, filters),
    getNextPageParam: (lastPage) =>
      (lastPage.number + 1) * lastPage.size < lastPage.total ? lastPage.number + 1 : undefined,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    enabled: selectedTab === 'scenarios',
  });

  const scenarios = data?.pages.flatMap((page) => page.content) ?? [];

  const { data: libraryData, fetchNextPage: fetchNextLibraryPage } = useInfiniteQuery({
    queryKey: ['libraryScenarios', filters, currentOrganisation?.id],
    queryFn: ({ pageParam }) => fetchLibraryScenarios(pageParam, SCENARIO_PAGE_SIZE, filters),
    getNextPageParam: (lastPage) =>
      (lastPage.number + 1) * lastPage.size < lastPage.total ? lastPage.number + 1 : undefined,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: selectedTab === 'library',
    placeholderData: keepPreviousData,
  });

  const libraryScenarios = libraryData?.pages.flatMap((page) => page.content) ?? [];

  const onFiltersUpdate = async (newFilters: ScenarioFiltersState) => {
    setFilters(newFilters);
  };

  return (
    <SalvPage
      title={'Monitoring Scenarios'}
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.MONITORING),
      }}
      actions={
        selectedTab === 'scenarios' && canAddScenario ? (
          <SalvButton
            label='New Scenario'
            icon={faPlus}
            onClick={() => setShowNewScenarioModal(true)}
            size={Size.SMALL}
          />
        ) : undefined
      }
      className='monitoring-scenarios'
    >
      <SalvTabs value={selectedTab} onChange={(value) => setSelectedTab(value)}>
        <SalvTabList>
          <SalvTab value={'scenarios'} title='My scenarios' />
          <SalvTab value={'library'} title='Library' />
        </SalvTabList>
        <div className='m-b-16 m-x-16'>
          <ScenarioFilters
            filters={filters}
            onFiltersUpdate={onFiltersUpdate}
            selectedTab={selectedTab}
            options={{
              statusOptions,
              typeOptions,
              categoryOptions,
            }}
          />
        </div>

        <SalvTabPanel value={'scenarios'}>
          <div aria-label={'Scenario Metrics'}>
            <SalvCard expansionOptions={{}} className='scenario-metrics-table m-b-16'>
              <SalvCard.Header>
                <div className='d-flex align-items-center'>
                  <span className='m-r-4 caps-xs text-secondary'>Most alerts generated (last 30 days)</span>
                  <SalvTooltipWrapper
                    tooltip={`Data updates dynamically based on any applied filters 
                  (e.g., filtering by type = 'periodic'), so you can see scenario contributions under different conditions.`}
                    placement='top'
                  >
                    <FontAwesomeIcon icon={faCircleInfo} className='cursor-pointer text-secondary' />
                  </SalvTooltipWrapper>
                </div>
              </SalvCard.Header>
              <SalvCard.Body>
                <SalvTable
                  emptyMessage='No alerts were created by selected scenarios.'
                  columns={scenariosMetricsColumns}
                  data={scenarioMetrics}
                />
              </SalvCard.Body>
            </SalvCard>
          </div>
          <SalvTable
            className='scenarios-table'
            hover={true}
            emptyMessage='No scenarios matching the specified criteria.'
            size={Size.MEDIUM}
            columns={scenariosColumns}
            rowClick={(row, e) => {
              if (e && (e.metaKey || e.ctrlKey)) {
                window.open(`/scenarios/${row.original.id}`, '_blank');
              } else {
                navigate(`/scenarios/${row.original.id}`, {
                  state: { search: location.search },
                });
              }
            }}
            data={scenarios}
            pagination={{
              type: SalvTablePaginationType.LOAD_MORE,
              total: data?.pages[0]?.total || 0,
              state: {
                pageIndex: data ? data.pages.length - 1 : 0,
                pageSize: SCENARIO_PAGE_SIZE,
              },
              setPagination: () => fetchNextPage(),
            }}
            sorting={{
              state: sorting,
              setSorting,
            }}
          />
        </SalvTabPanel>
        <SalvTabPanel value={'library'}>
          <SalvTable
            columnVisibility={{ status: false }}
            className='scenarios-table'
            hover={true}
            emptyMessage='Scenario library is empty.'
            size={Size.MEDIUM}
            columns={libraryScenariosColumns}
            rowClick={(row, e) => {
              if (e && (e.metaKey || e.ctrlKey)) {
                window.open(`/salv-scenarios/${row.original.id}`, '_blank');
              } else {
                navigate(`/salv-scenarios/${row.original.id}`, {
                  state: { search: location.search },
                });
              }
            }}
            data={libraryScenarios}
            pagination={{
              type: SalvTablePaginationType.LOAD_MORE,
              total: libraryData?.pages[0]?.total || 0,
              state: {
                pageIndex: libraryData ? libraryData.pages.length - 1 : 0,
                pageSize: SCENARIO_PAGE_SIZE,
              },
              setPagination: () => fetchNextLibraryPage(),
            }}
          />
        </SalvTabPanel>
      </SalvTabs>
      <NewScenarioModal
        showModal={showNewScenarioModal}
        onClose={() => setShowNewScenarioModal(false)}
        onCreate={(scenarioId) => {
          navigate('/scenarios/' + scenarioId, {
            state: { search: location.search },
          });
        }}
      />
    </SalvPage>
  );
};

export default MonitoringScenarios;
